import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';

import { MultiCurrencyPrice } from '@mews-distributor/common';

import { getCurrencyCode } from '../../modules/app/selectors';
import { isMultiHotel } from '../../modules/configuration/selectors';
import {
    getEnterpriseDefaultLanguageCode,
    getEnterpriseDefaultRateCurrencyCode,
    getEnterpriseName,
} from '../../modules/entities/enterprises/selectors';
import { convertPrice } from '../../modules/entities/exchangeRates/viewSelectors';
import { getCurrencyCodes, localizeProperty } from '../../modules/globalization/selectors';
import { getSelectedEnterpriseCategoryLowestPrice } from '../../modules/pricing/views/categoriesPricing';
import { getCurrentDateServiceCategoryRateIds } from '../../modules/pricing/views/servicePricing';
import { getCategoryName } from '../../modules/resourceCategories/selectors';
import { ApplicationState } from '../../types';
import { Category } from '../../types/data';
import { WithEnterpriseId } from '../../types/selectors/selectorPropTypes';
import { AvailableRoom } from './types';

interface GetMultiCurrencyPriceProps {
    value: number;
    enterpriseId: string;
    currencyCode: string;
}

const getMultiCurrencyPrice = (
    state: ApplicationState,
    { value, enterpriseId, currencyCode }: GetMultiCurrencyPriceProps
) =>
    pipe(
        getCurrencyCodes(state),
        array.reduce<string, MultiCurrencyPrice>({}, (acc, targetCurrencyCode) => {
            acc[targetCurrencyCode] = convertPrice(state, {
                enterpriseId,
                value,
                currencyCode,
                sourceCurrencyCode: currencyCode,
                targetCurrencyCode,
            });

            return acc;
        })
    );

interface WithCategories {
    categories: Category[];
}

export const getImpressions = (
    state: ApplicationState,
    { enterpriseId, categories }: WithEnterpriseId & WithCategories
) =>
    pipe(
        categories,
        array.mapWithIndex((position, category) => {
            const name = localizeProperty(state, {
                property: getCategoryName(state, { categoryId: category.id }),
                languageCode: getEnterpriseDefaultLanguageCode(state, { enterpriseId }),
            });

            return {
                id: category.id,
                name,
                position,
            };
        })
    );

export const getAvailableRooms = (
    state: ApplicationState,
    { enterpriseId, categories }: WithEnterpriseId & WithCategories
): AvailableRoom[] => {
    const defaultLanguageCode: string = getEnterpriseDefaultLanguageCode(state, { enterpriseId });
    const defaultRateCurrencyCode = getEnterpriseDefaultRateCurrencyCode(state, { enterpriseId });
    const currencyCode = getCurrencyCode(state);
    if (currencyCode == null) {
        return [];
    }

    return pipe(
        categories,
        array.map((category) => {
            const categoryName = getCategoryName(state, { categoryId: category.id });
            const availableRateIds = getCurrentDateServiceCategoryRateIds(state, { categoryIds: [category.id] });

            const lowestCategoryPrice = getSelectedEnterpriseCategoryLowestPrice(state, {
                categoryId: category.id,
            });
            const convertToDefaultRateCurrency = (value: number) =>
                convertPrice(state, {
                    enterpriseId,
                    value,
                    currencyCode,
                    sourceCurrencyCode: currencyCode,
                    targetCurrencyCode: defaultRateCurrencyCode,
                });

            const lowestNetPrice = lowestCategoryPrice?.[currencyCode]?.netValue ?? null;
            const lowestPrice = lowestCategoryPrice?.[currencyCode]?.grossValue ?? null;

            const netPrice =
                lowestNetPrice !== null
                    ? getMultiCurrencyPrice(state, { value: lowestNetPrice, currencyCode, enterpriseId })
                    : null;
            const price =
                lowestPrice !== null
                    ? getMultiCurrencyPrice(state, { value: lowestPrice, currencyCode, enterpriseId })
                    : null;
            const roomName = localizeProperty(state, { property: categoryName, languageCode: defaultLanguageCode });

            return {
                roomName,
                roomId: category.id,
                availableRateIds,
                netPrice,
                price,
                lowestNetPrice: lowestNetPrice != null ? convertToDefaultRateCurrency(lowestNetPrice) : null,
                lowestPrice: lowestPrice != null ? convertToDefaultRateCurrency(lowestPrice) : null,
            };
        })
    );
};

interface MultiPropertyData {
    hotelId: string;
    hotelName: string;
}

export const getMultiPropertyData = (
    state: ApplicationState,
    { enterpriseId }: WithEnterpriseId
): MultiPropertyData | {} => {
    if (!isMultiHotel(state)) {
        return {};
    }
    const defaultLanguageCode: string = getEnterpriseDefaultLanguageCode(state, { enterpriseId });
    const enterpriseName = getEnterpriseName(state, { enterpriseId });
    const hotelName = localizeProperty(state, {
        property: enterpriseName,
        languageCode: defaultLanguageCode,
    });

    return {
        hotelId: enterpriseId,
        hotelName,
    };
};
